import type { AuthSession } from "./types";
import { authenticatedFetch } from "./auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;

export type LocalBridgeState =
  | "provisioning"
  | "awaiting_qr"
  | "authenticated"
  | "ready"
  | "degraded"
  | "offline"
  | "error"
  | "paused"
  | "revoked";

export type LocalBridgeConnection = {
  connectionId: string;
  organizationId: string;
  tenantId: string;
  label: string;
  adapter: "wwebjs" | "baileys";
  deviceLabel: string;
  captureMode: "group_allowlist" | "resident_allowlist";
  status: "active" | "paused" | "revoked";
  state: LocalBridgeState;
  phone?: string;
  displayName?: string;
  host?: string;
  bridgeVersion?: string;
  libraryVersion?: string;
  lastHeartbeatAt?: string;
  lastGroupSyncAt?: string;
  discoveredGroupCount?: number;
  lastError?: string;
  mediaCaptureEnabled?: boolean;
  mediaCaptureActive?: boolean;
  historyImport?: {
    since: string;
    status: "pending" | "receiving" | "complete" | "unavailable";
    importedMessages: number;
    requestedAt: string;
    completedAt?: string;
    detail?: string;
  };
  metrics?: {
    rssMb?: number;
    heapUsedMb?: number;
    uptimeSeconds?: number;
    pendingEvents?: number;
    capturedMessages?: number;
    storedMedia?: number;
    mediaFailures?: number;
    reconnects?: number;
    enabledGroups?: number;
    enabledResidents?: number;
  };
  createdAt?: string;
  updatedAt?: string;
};

export type LocalBridgeGroup = {
  connectionId: string;
  organizationId: string;
  groupId: string;
  name: string;
  participantCount: number;
  readOnly: boolean;
  isCommunity: boolean;
  enabled: boolean;
  customerId?: string;
  customer?: string;
  site?: string;
  retentionDays?: number;
  privacyNoticeAcceptedAt?: string;
  privacyNoticeVersion?: string;
  privacyNoticeAcceptedBy?: string;
  privacyNoticeAcceptedByEmail?: string;
  updatedAt?: string;
};

export type LocalBridgeResident = {
  connectionId: string;
  organizationId: string;
  phone: string;
  displayName: string;
  customerId?: string;
  customer?: string;
  site?: string;
  externalResidentId?: string;
  facilityId?: string;
  enabled: boolean;
  privacyNoticeAcceptedAt?: string;
  privacyNoticeVersion?: string;
  updatedAt?: string;
};

export type LocalBridgePaseFirmeConfig = {
  configured: boolean;
  enabled: boolean;
  baseUrl: string;
  configuredAt?: string;
  webhookPath: string;
  webhookSecret?: string;
};

export type LocalBridgeTranscriptionProvider = {
  provider: "openai";
  model: "gpt-transcribe";
  mediaReviewModel?: "gpt-4.1-mini";
  scope?: "pgr-operational-groups";
  configured: boolean;
  configuredAt?: string;
};

export type LocalBridgeQr = {
  available: boolean;
  state: LocalBridgeState;
  dataUrl?: string;
  expiresAt?: string;
};

export type LocalBridgeCommand = {
  commandId: string;
  type: string;
  status: "pending" | "delivered" | "done" | "failed";
  createdAt?: string;
  completedAt?: string;
  resultDetail?: string;
};

export async function listLocalBridgeConnections(session: AuthSession) {
  const response = await request<{ items: LocalBridgeConnection[] }>("/bridge/connections", session);
  return response.items;
}

export async function createLocalBridgeConnection(
  input: { label: string; deviceLabel: string; adapter?: "wwebjs" | "baileys"; captureMode?: "group_allowlist" | "resident_allowlist" },
  session: AuthSession,
) {
  return request<{ item: LocalBridgeConnection; token: string }>("/bridge/connections", session, {
    method: "POST",
    body: JSON.stringify({ ...input, adapter: input.adapter ?? "baileys", captureMode: input.captureMode ?? "group_allowlist" }),
  });
}

export async function updateLocalBridgeConnection(
  connectionId: string,
  input: { label?: string; status?: "active" | "paused" | "revoked"; mediaCaptureEnabled?: boolean },
  session: AuthSession,
) {
  const response = await request<{ item: LocalBridgeConnection }>(
    `/bridge/connections/${encodeURIComponent(connectionId)}`,
    session,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return response.item;
}

export async function transferLocalBridgeConnection(
  connectionId: string,
  input: { targetOrganizationId: string; confirmConnectionId: string; label?: string },
  session: AuthSession,
) {
  const response = await request<{ item: LocalBridgeConnection }>(
    `/bridge/connections/${encodeURIComponent(connectionId)}/transfer`,
    session,
    { method: "POST", body: JSON.stringify(input) },
  );
  return response.item;
}

export async function rotateLocalBridgeToken(connectionId: string, session: AuthSession) {
  return request<{ item: LocalBridgeConnection; token: string }>(
    `/bridge/connections/${encodeURIComponent(connectionId)}/rotate-token`,
    session,
    { method: "POST" },
  );
}

export async function getLocalBridgeQr(connectionId: string, session: AuthSession) {
  return request<LocalBridgeQr>(
    `/bridge/connections/${encodeURIComponent(connectionId)}/qr`,
    session,
  );
}

export async function listLocalBridgeGroups(connectionId: string, session: AuthSession) {
  const response = await request<{ items: LocalBridgeGroup[] }>(
    `/bridge/connections/${encodeURIComponent(connectionId)}/groups`,
    session,
  );
  return response.items;
}

export async function updateLocalBridgeGroup(
  connectionId: string,
  groupId: string,
  input: { enabled?: boolean; customerId?: string; customer?: string; site?: string; retentionDays?: number; privacyNoticeAccepted?: true },
  session: AuthSession,
) {
  const response = await request<{ item: LocalBridgeGroup }>(
    `/bridge/connections/${encodeURIComponent(connectionId)}/groups/${encodeURIComponent(groupId)}`,
    session,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  return response.item;
}

export async function listLocalBridgeResidents(connectionId: string, session: AuthSession) {
  const response = await request<{ items: LocalBridgeResident[] }>(
    `/bridge/connections/${encodeURIComponent(connectionId)}/residents`,
    session,
  );
  return response.items;
}

export async function upsertLocalBridgeResident(
  connectionId: string,
  phone: string,
  input: { displayName: string; customerId?: string; customer?: string; site?: string; externalResidentId?: string; facilityId?: string; enabled: boolean; privacyNoticeAccepted?: true },
  session: AuthSession,
) {
  const response = await request<{ item: LocalBridgeResident }>(
    `/bridge/connections/${encodeURIComponent(connectionId)}/residents/${encodeURIComponent(phone)}`,
    session,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return response.item;
}

export async function getLocalBridgePaseFirmeConfig(connectionId: string, session: AuthSession) {
  return request<LocalBridgePaseFirmeConfig>(
    `/bridge/connections/${encodeURIComponent(connectionId)}/pasefirme`,
    session,
  );
}

export async function configureLocalBridgePaseFirme(
  connectionId: string,
  input: { baseUrl: string; apiKey?: string; webhookSecret?: string; enabled: boolean },
  session: AuthSession,
) {
  return request<LocalBridgePaseFirmeConfig>(
    `/bridge/connections/${encodeURIComponent(connectionId)}/pasefirme`,
    session,
    { method: "PUT", body: JSON.stringify(input) },
  );
}

export async function getLocalBridgeTranscriptionProvider(session: AuthSession) {
  return request<LocalBridgeTranscriptionProvider>("/bridge/transcription-provider", session);
}

export async function configureLocalBridgeTranscriptionProvider(apiKey: string, session: AuthSession) {
  return request<LocalBridgeTranscriptionProvider>("/bridge/transcription-provider", session, {
    method: "PUT",
    body: JSON.stringify({ apiKey }),
  });
}

export function localBridgeWebhookUrl(path: string) {
  if (!API_BASE_URL) return path;
  return `${API_BASE_URL.replace(/\/$/, "")}${path.startsWith("/") ? path : `/${path}`}`;
}

export async function listLocalBridgeCommands(connectionId: string, session: AuthSession) {
  const response = await request<{ items: LocalBridgeCommand[] }>(
    `/bridge/connections/${encodeURIComponent(connectionId)}/commands`,
    session,
  );
  return response.items;
}

export async function createLocalBridgeCommand(
  connectionId: string,
  input:
    | { type: "send_text"; groupId: string; body: string }
    | { type: "send_direct_text"; recipientPhone: string; body: string }
    | { type: "refresh_groups" }
    | { type: "import_history"; since: string }
    | { type: "restart" }
    | { type: "logout" }
    | { type: "standby" }
    | { type: "resume" },
  session: AuthSession,
) {
  const response = await request<{ item: LocalBridgeCommand }>(
    `/bridge/connections/${encodeURIComponent(connectionId)}/commands`,
    session,
    { method: "POST", body: JSON.stringify(input) },
  );
  return response.item;
}

async function request<T>(path: string, session: AuthSession, init: RequestInit = {}): Promise<T> {
  if (!API_BASE_URL) throw new Error("API_NOT_CONFIGURED");
  const response = await authenticatedFetch(`${API_BASE_URL}${path}`, session, init);
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const code = typeof body?.error === "string" ? body.error : `HTTP_${response.status}`;
    throw new Error(code);
  }
  return body as T;
}
